import React, { useContext, useRef } from 'react';
import { FiX, FiPrinter, FiShare2, FiRefreshCcw } from 'react-icons/fi';
import AuthContext from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';

const ReceiptModal = ({ isOpen, onClose, sale }) => {
  const { user } = useContext(AuthContext);
  const receiptRef = useRef();

  if (!isOpen || !sale) return null;

  const subtotal = (sale.totalAmount || 0) + (sale.discount || 0);

  const handlePrint = () => {
    window.print();
  };

  const handleWhatsAppShare = async () => {
    if (!receiptRef.current) return;

    try {
      toast.info("Génération de l'image du ticket...");
      const canvas = await html2canvas(receiptRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], `ticket-${sale._id.slice(-6)}.png`, { type: 'image/png' });

        // Le partage de fichiers fonctionne principalement sur Mobile
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: `Ticket - ${user?.shopName || 'Boutique'}`,
            });
          } catch (err) {
            if (err.name !== 'AbortError') toast.error("Erreur lors du partage.");
          }
        } else {
          // Fallback pour Desktop : téléchargement de l'image
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `ticket-${sale._id.slice(-6)}.png`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success("Image téléchargée. Vous pouvez l'envoyer manuellement.");
        }
      }, 'image/png');
    } catch (error) {
      console.error("Erreur de génération d'image:", error);
      toast.error("Impossible de générer l'image du ticket.");
    }
  };

  const handleRefund = async () => {
    if (!window.confirm("Voulez-vous vraiment annuler cette vente et rembourser le client ? Le stock sera rétabli.")) return;
    try {
        await api.post(`/sales/${sale._id}/refund`);
        toast.success("Vente annulée et stock rétabli.");
        onClose();
    } catch (error) {
        const errorMessage = error.response?.data?.message || "Erreur lors de l'annulation de la vente.";
        toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #receipt-content, #receipt-content * { visibility: visible; }
            #receipt-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print { display: none !important; }
          }
        `}
      </style>
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm flex flex-col">
        <div className="flex justify-between items-center mb-4 no-print">
          <h3 className="text-xl font-semibold">Ticket de Caisse</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <FiX size={24} />
          </button>
        </div>

        {/* Contenu du ticket */}
        <div 
          id="receipt-content" 
          ref={receiptRef} 
          className="bg-white border border-gray-200 rounded-lg font-mono text-sm"
          style={{ background: 'white', padding: '20px' }}
        >
          <div className="text-center mb-4">
            {user?.logoUrl && (
                <img src={user.logoUrl} alt="Logo" className="h-16 mx-auto mb-2 object-contain" />
            )}
            <h4 className="text-lg font-bold">{user?.shopName || 'Votre Boutique'}</h4>
            {user?.address && <p className="text-xs">{user.address}</p>}
            {user?.phone && <p className="text-xs">Tél: {user.phone}</p>}
          </div>
          <div className="border-b border-dashed pb-2 mb-2 text-xs">
            <p>Date: {new Date(sale.date).toLocaleString('fr-FR')}</p>
            <p>Ticket #: {sale._id.slice(-6).toUpperCase()}</p>
            {sale.clientId?.name && (
              <div className="mt-1">
                <p className="font-semibold">Client: {sale.clientId.name}</p>
                {sale.clientId.phone && <p>Tél: {sale.clientId.phone}</p>}
              </div>
            )}
          </div>
          <table className="w-full mb-2">
            <thead>
              <tr className="border-b border-dashed">
                <th className="text-left font-normal py-1">PRODUIT</th>
                <th className="text-right font-normal py-1">QTE</th>
                <th className="text-right font-normal py-1">P.U.</th>
                <th className="text-right font-normal py-1">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {sale.items.map(item => (
                <tr key={item.productId}>
                  <td className="py-1">{item.name}</td>
                  <td className="text-right py-1">{item.quantity}</td>
                  <td className="text-right py-1">{item.salePrice.toLocaleString('fr-FR')}</td>
                  <td className="text-right py-1">{(item.quantity * item.salePrice).toLocaleString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t-2 border-dashed pt-2 mt-2">
            {sale.discount > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span>Sous-total</span>
                  <span>{subtotal.toLocaleString('fr-FR')} CFA</span>
                </div>
                <div className="flex justify-between text-sm text-red-600">
                  <span>Remise</span>
                  <span>-{(sale.discount || 0).toLocaleString('fr-FR')} CFA</span>
                </div>
              </>
            )}
            <div className="flex justify-between font-bold text-base mt-1">
              <span>TOTAL</span>
              <span>{sale.totalAmount.toLocaleString('fr-FR')} CFA</span>
            </div>
            {sale.isCreditSale && (
              <div className="text-center mt-2 text-red-600 font-semibold">
                VENTE À CRÉDIT
              </div>
            )}
          </div>
          <div className="text-center mt-4 text-xs text-gray-600">
            <p>Merci de votre visite !</p>
            <p>Jaapand-e</p>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 no-print">
          <button onClick={handleRefund} className="bg-red-50 hover:bg-red-100 text-red-600 font-bold py-2 px-3 rounded-lg flex items-center justify-center w-full sm:w-auto border border-red-200" title="Annuler la vente">
            <FiRefreshCcw size={20} />
          </button>
          <button onClick={handleWhatsAppShare} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-3 rounded-lg flex items-center justify-center w-full text-sm">
            <FiShare2 className="mr-2" /> WhatsApp
          </button>
          <button onClick={handlePrint} className="bg-brand-blue hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-lg flex items-center justify-center w-full text-sm">
            <FiPrinter className="mr-2" />
            Imprimer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;